import React, { useState } from 'react';
import {
    Link,
    useRecordContext,
    useTranslate,
    useNotify,
    useRefresh,
} from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import { IconButton, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { red } from '@mui/material/colors';

interface Props {
    showRemove?: boolean;
    showDebug?: boolean;
    label?: string;
    size?: 'small' | 'large';
}

const SourceNameField = ({ showRemove = false, showDebug = false, label = '', size }: Props) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [saving, setSaving] = useState(false);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonData, setJsonData] = useState(null);
    const refresh = useRefresh();

    const record = useRecordContext();
    const { isLoading, canAccess } = useCanAccess({ action: 'debug', resource: 'user', record });
    if (isLoading || !record) return null;
    //console.log('canAccess', canAccess);
    
    // Get unique services by grouping them by display name
    const getDisplayName = (service: string) => {
        if (service === 'crm' || service === 'mpass') return service;
        return service
            .replace(/opepalvelu/g, 'oop')
            .replace(/fl_opettaja/g, 'fl')
            .replace(/fl_opiskelija/g, 'fl')
            .replace(/fl_opepalvelu/g, 'fl')
            .replace(/finnlectura/g, 'fl')
            .replace(/nova_.*/g, 'nova')
            .replace(
                /opiskelija/g,
                record?.username?.startsWith('e') && record?.username?.length < 10 ? 'kv' : 'oop'
            )
            .replace(/adazure/g, 'ad')
            .toUpperCase();
    };
    
    // Create map of display name -> original service (keeping first occurrence)
    const uniqueServices = new Map<string, string>();
    record?.services?.forEach((service: string) => {
        const displayName = getDisplayName(service);
        if (!uniqueServices.has(displayName)) {
            uniqueServices.set(displayName, service);
        }
    });
    
    return (
        <>
            <Stack
                direction="row"
                alignItems={'baseline'}
                spacing={1}
                sx={{ fontSize: size === 'small' ? '0.7rem' : '1rem' }}
            >
                {Array.from(uniqueServices.entries()).map(([displayName, service], i) => {
                    switch (service) {
                        case 'crm':
                            return (
                                <Tooltip
                                    title={translate('resources.user.actions.open_crm')}
                                    key={`${record.guid}_${i}${service}`}
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: 'white',
                                                color: 'text.primary',
                                                boxShadow: 1,
                                                border: '1px solid #e0e0e0',
                                            },
                                        },
                                    }}
                                >
                                    <span>
                                        <Link
                                            to={`${process.env.REACT_APP_CRM_URL}/main.aspx?appid=b68a0d15-8769-4bc0-8999-f949f7404bbb&pagetype=entityrecord&etn=contact&id=${record.crm_guid}`}
                                            target="_blank"
                                            onClick={e => e.stopPropagation()}
                                            style={{
                                                textDecoration: 'none',
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: '#fff',
                                                    padding: '1px 6px',
                                                    borderRadius: 3,
                                                    background: 'rgb(96, 125, 139, .5)',
                                                    transition: 'box-shadow 0.3s',
                                                }}
                                                onMouseEnter={e =>
                                                    (e.currentTarget.style.boxShadow =
                                                        '0 3px 6px rgba(0, 0, 0, 0.2)')
                                                }
                                                onMouseLeave={e =>
                                                    (e.currentTarget.style.boxShadow =
                                                        'none')
                                                }
                                            >
                                                {service.toUpperCase()}
                                            </span>
                                        </Link>
                                    </span>
                                </Tooltip>
                            );
                        case 'mpass':
                            return (
                                <div key={`${record.guid}_${i}${service}`}>
                                    <span
                                        style={{
                                            textDecoration: 'none',
                                            color: '#fff',
                                            padding: '1px 6px',
                                            borderRadius: 3,
                                            background: 'rgb(96, 125, 139, .5)',
                                        }}
                                    >
                                        {service.toUpperCase()}
                                    </span>
                                    {showRemove && (
                                        <Tooltip
                                            title={translate(
                                                'resources.user.actions.remove_mpass'
                                            )}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: 'white',
                                                        color: 'text.primary',
                                                        boxShadow: 1,
                                                        border: '1px solid #e0e0e0',
                                                    },
                                                },
                                            }}
                                        >
                                            <IconButton
                                                style={{ overflow: 'inherit' }}
                                                disabled={saving}
                                                onClick={() => {
                                                    let action = 'REMOVE_MPASS';
                                                    let data = {
                                                        ...record,
                                                        ...{ action },
                                                    };
                                                    setSaving(true);
                                                    fetch(
                                                        `${process.env.REACT_APP_INFRA_API}/teacher/${record.guid}`,
                                                        {
                                                            method: 'PUT',
                                                            headers: {
                                                                Accept: 'application/json',
                                                                'Content-Type':
                                                                    'application/json',
                                                                school: localStorage.getItem(
                                                                    'school'
                                                                ),
                                                                authorization:
                                                                    'Bearer ' +
                                                                    localStorage.getItem(
                                                                        'token'
                                                                    ),
                                                            },
                                                            body: JSON.stringify(data),
                                                        }
                                                    ).then(ret => {
                                                        setSaving(false);
                                                        if (ret.status !== 200) {
                                                            notify(
                                                                translate(
                                                                    'notifications.ACTION_ERROR'
                                                                ),
                                                                { type: 'error' }
                                                            );
                                                        } else {
                                                            /*All good*/
                                                            setSaving(false);
                                                            notify(
                                                                translate(
                                                                    'notifications.MPASS_REMOVED'
                                                                ),
                                                                { type: 'success' }
                                                            );
                                                            refresh();
                                                        }
                                                    });
                                                }}
                                            >
                                                <DeleteIcon
                                                    style={{ color: red[500] }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {showDebug && canAccess && process.env.REACT_APP_MPASS_DEBUG_LINKS_ENABLED === 'true' && (
                                        <>
                                            <Tooltip
                                                title={translate(
                                                    'resources.user.actions.debug_mpass_original'
                                                )}
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'white',
                                                            color: 'text.primary',
                                                            boxShadow: 1,
                                                            border: '1px solid #e0e0e0',
                                                        },
                                                    },
                                                }}
                                            >
                                                <IconButton
                                                    style={{ overflow: 'inherit', marginLeft: '-8px' }}
                                                    disabled={saving}
                                                    onClick={() => {
                                                        let action = 'DEBUG_MPASS';
                                                        let data = {
                                                            ...record,
                                                            ...{ action },
                                                        };
                                                        setSaving(true);
                                                        fetch(
                                                            `${process.env.REACT_APP_INFRA_API}/teacher/${record.guid}`,
                                                            {
                                                                method: 'PUT',
                                                                headers: {
                                                                    Accept: 'application/json',
                                                                    'Content-Type':
                                                                        'application/json',
                                                                    school: localStorage.getItem(
                                                                        'school'
                                                                    ),
                                                                    authorization:
                                                                        'Bearer ' +
                                                                        localStorage.getItem(
                                                                            'token'
                                                                        ),
                                                                },
                                                                body: JSON.stringify(data),
                                                            }
                                                        ).then(ret => {
                                                            setSaving(false);
                                                            if (ret.status !== 200) {
                                                                notify(
                                                                    translate(
                                                                        'notifications.ACTION_ERROR'
                                                                    ),
                                                                    { type: 'error' }
                                                                );
                                                            } else {
                                                                /*All good*/
                                                                setSaving(false);
                                                                ret.json().then(data => {
                                                                    if (data.link) {
                                                                        window.open(data.link, '_blank');
                                                                    }
                                                                });
                                                                refresh();
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <PersonIcon
                                                        style={{ color: '#1976d2' }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                title={translate(
                                                    'resources.user.actions.debug_mpass_new'
                                                )}
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'white',
                                                            color: 'text.primary',
                                                            boxShadow: 1,
                                                            border: '1px solid #e0e0e0',
                                                        },
                                                    },
                                                }}
                                            >
                                                <IconButton
                                                    style={{ overflow: 'inherit', marginLeft: '-16px' }}
                                                    disabled={saving}
                                                    onClick={() => {
                                                        let action = 'DEBUG_MPASS';
                                                        let data = {
                                                            ...record,
                                                            ...{ action },
                                                        };
                                                        setSaving(true);
                                                        fetch(
                                                            `${process.env.REACT_APP_INFRA_API}/teacher/${record.guid}`,
                                                            {
                                                                method: 'PUT',
                                                                headers: {
                                                                    Accept: 'application/json',
                                                                    'Content-Type':
                                                                        'application/json',
                                                                    school: localStorage.getItem(
                                                                        'school'
                                                                    ),
                                                                    authorization:
                                                                        'Bearer ' +
                                                                        localStorage.getItem(
                                                                            'token'
                                                                        ),
                                                                },
                                                                body: JSON.stringify(data),
                                                            }
                                                        ).then(ret => {
                                                            setSaving(false);
                                                            if (ret.status !== 200) {
                                                                notify(
                                                                    translate(
                                                                        'notifications.ACTION_ERROR'
                                                                    ),
                                                                    { type: 'error' }
                                                                );
                                                            } else {
                                                                /*All good*/
                                                                setSaving(false);
                                                                ret.json().then(data => {
                                                                    if (data.new_link) {
                                                                        window.open(data.new_link, '_blank');
                                                                    }
                                                                });
                                                                refresh();
                                                            }
                                                        });
                                                    }}
                                                >
                                                    <PersonIcon
                                                        style={{ color: '#4caf50' }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    
                                    {showDebug && canAccess && process.env.REACT_APP_MPASS_DEBUG_DATA_ENABLED === 'true' && (
                                        <Tooltip
                                            title={translate(
                                                'resources.user.actions.debug_mpass_json'
                                            )}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: 'white',
                                                        color: 'text.primary',
                                                        boxShadow: 1,
                                                        border: '1px solid #e0e0e0',
                                                    },
                                                },
                                            }}
                                        >
                                            <IconButton
                                                style={{ overflow: 'inherit', marginLeft: '-16px' }}
                                                disabled={saving}
                                                onClick={() => {
                                                    let action = 'DEBUG_MPASS';
                                                    let data = {
                                                        ...record,
                                                        ...{ action },
                                                    };
                                                    setSaving(true);
                                                    fetch(
                                                        `${process.env.REACT_APP_INFRA_API}/teacher/${record.guid}`,
                                                        {
                                                            method: 'PUT',
                                                            headers: {
                                                                Accept: 'application/json',
                                                                'Content-Type':
                                                                    'application/json',
                                                                school: localStorage.getItem(
                                                                    'school'
                                                                ),
                                                                authorization:
                                                                    'Bearer ' +
                                                                    localStorage.getItem(
                                                                        'token'
                                                                    ),
                                                            },
                                                            body: JSON.stringify(data),
                                                        }
                                                    ).then(ret => {
                                                        setSaving(false);
                                                        if (ret.status !== 200) {
                                                            notify(
                                                                translate(
                                                                    'notifications.ACTION_ERROR'
                                                                ),
                                                                { type: 'error' }
                                                            );
                                                        } else {
                                                            /*All good*/
                                                            setSaving(false);
                                                            ret.json().then(data => {
                                                                setJsonData(data);
                                                                setJsonDialogOpen(true);
                                                            });
                                                            refresh();
                                                        }
                                                    });
                                                }}
                                            >
                                                <DataObjectIcon
                                                    style={{ color: '#ff9800' }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                </div>
                            );
                        case 'adazure':
                        case 'opepalvelu':
                        case 'finnlectura':
                        case 'opiskelija':
                        case 'edustore':
                        case 'fl_opepalvelu':
                        case 'fl_opiskelija':
                        case 'fl_opettaja':
                        case 'nova_opiskelija':
                        case 'nova_opettaja':
                        case 'nova_oppilas':
                            return (
                                <div key={`${record.guid}_${i}${service}`}>
                                    <span
                                        style={{
                                            textDecoration: 'none',
                                            color: '#fff',
                                            padding: '1px 6px',
                                            borderRadius: 3,
                                            background: 'rgb(96, 125, 139, .5)',
                                        }}
                                    >
                                        {displayName}
                                    </span>
                                </div>
                            );
                        default:
                            return null;
                    }
                })}
            </Stack>
            
            <Dialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>MPASS Debug Data</DialogTitle>
                <DialogContent>
                    <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '16px', 
                        borderRadius: '4px',
                        overflow: 'auto',
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap'
                    }}>
                        {jsonData ? JSON.stringify(jsonData, null, 2) : ''}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setJsonDialogOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SourceNameField;